/**
 * Provides the rendering context for ContextBar widgets (SmartTodos,
 * DocumentsLinksAndSimilar, MailSummary, etc.).
 *
 * Distinct from deepFocusStore: this context answers "is this component tree
 * being rendered inside a Deep Focus layout?" — a local, structural question
 * derived from the ContextBar variant prop. A widget can be mounted in a
 * CustomView (always false) or inside a DeepFocusView (true), independently
 * of whether the global Deep Focus mode is currently active.
 *
 * Use deepFocusStore for global behaviour (toggling, music pause, navigation
 * reset). Use this context for widget styling only.
 */
import { createContext, ReactNode, useContext, useMemo } from "react";

type ContextBarAppearanceContextType = {
    isDeepFocus: boolean;
};

const ContextBarAppearanceContext =
    createContext<ContextBarAppearanceContextType>({
        isDeepFocus: false,
    });

type ContextBarAppearanceProviderProps = {
    isDeepFocus: boolean;
    children: ReactNode;
};

export const ContextBarAppearanceProvider = ({
    isDeepFocus,
    children,
}: ContextBarAppearanceProviderProps) => {
    const value = useMemo(() => ({ isDeepFocus }), [isDeepFocus]);

    return (
        <ContextBarAppearanceContext.Provider value={value}>
            {children}
        </ContextBarAppearanceContext.Provider>
    );
};

export const useContextBarAppearance = () =>
    useContext(ContextBarAppearanceContext);
